// The match log, and the replay it doubles as.
//
// Two jobs, one record. On screen it answers "what just happened, and why did
// the opponent do that" -- an entry per action, with the events it actually
// caused underneath it. On disk it is a replay, and that costs almost nothing
// here: the engine is deterministic and the whole match derives from the seed,
// so the decks, the shuffle and every draw come back from `{ seed, actions }`.
// Nothing about the board state needs saving, and a replay that stored one
// could disagree with the engine that produced it.

import type {
  Action, GameEvent, GameState, Location, PlayerId,
} from '../rules/types.ts';
import type { DeckList } from '../rules/engine.ts';
import type { LoadedRules } from '../rules/load.ts';
import type { AiTrace } from '../ai/opponent.ts';

/** Bumped when the shape below changes in a way older files cannot satisfy. */
export const REPLAY_VERSION = 1;

export interface Replay {
  version: number;
  /** The seed string, which is the whole match: decks, shuffle and draws. */
  seed: string;
  difficulty: string;
  human: PlayerId;
  /** Every action applied, in order, by whichever side took it. */
  actions: Action[];
  /** For the file listing only; the engine never reads these back. */
  recorded: string;
  result?: { winner: PlayerId | null; points: [number, number]; turns: number };
  /**
   * Present only when the decks were chosen rather than dealt by the seed.
   *
   * Its absence is the interesting half: a replay with no `decks` is one the
   * seed reproduces on its own, which is the property the format is built on.
   */
  decks?: [DeckList, DeckList];
  /**
   * The numeric seed the game was created from, when it cannot be derived.
   *
   * Ordinarily the seed *string* gives both the decks and the shuffle: the
   * deck builder draws from the same stream, so where it stops is where the
   * shuffle starts. A match whose decks came from somewhere else -- a server
   * match, which builds them its own way -- has no such relationship, so the
   * number is written down alongside the decks. Optional, and absent on every
   * replay the seed alone reproduces, which keeps the ordinary case honest.
   */
  startSeed?: number;
}

export interface LogEntry {
  n: number;
  turn: number;
  phase: string;
  side: 'you' | 'opponent';
  /** What was done, in the words a player would use. */
  action: string;
  /** Why, when the actor can say. Only the AI can. */
  why: string | null;
  /** What actually followed, from the engine's own event stream. */
  effects: string[];
}

export class MatchLog {
  readonly entries: LogEntry[] = [];
  private readonly rules: LoadedRules;
  private readonly human: PlayerId;
  /** Every action applied, in order. Public so a rewind can replay them. */
  readonly actions: Action[] = [];
  private seq = 0;
  /** Bounded so a long match cannot grow the page without limit. */
  private readonly limit: number;

  constructor(rules: LoadedRules, human: PlayerId, limit = 4000) {
    this.rules = rules;
    this.human = human;
    this.limit = limit;
  }

  /** Records one applied action and everything it caused. */
  record(
    state: GameState, actor: PlayerId, action: Action,
    events: readonly GameEvent[], trace?: AiTrace | null,
  ): void {
    this.actions.push(action);
    this.entries.push({
      n: ++this.seq,
      turn: state.turn,
      phase: state.phase,
      side: actor === this.human ? 'you' : 'opponent',
      action: this.describeAction(state, action),
      why: trace ? describeTrace(trace) : null,
      effects: events.map((e) => this.describeEvent(e)).filter((s): s is string => s !== null),
    });
    if (this.entries.length > this.limit) this.entries.splice(0, this.entries.length - this.limit);
  }

  /**
   * `decks` is passed only when the match did not deal them from the seed.
   *
   * A replay stores a seed and a list of actions and nothing else, because the
   * engine is deterministic and everything else follows -- but that stops being
   * true the moment a deck is *chosen*, either the player's own or the one the
   * AI was asked to practise against. Recording them then is what keeps the
   * claim honest; recording them always would make every replay bigger and
   * able to contradict the engine that produced it.
   */
  replay(
    seed: string, difficulty: string, state: GameState,
    decks?: readonly [DeckList, DeckList],
    startSeed?: number,
  ): Replay {
    const over = state.waiting.kind === 'gameOver' ? state.waiting : null;
    return {
      version: REPLAY_VERSION,
      seed,
      difficulty,
      human: this.human,
      actions: this.actions,
      recorded: new Date().toISOString(),
      ...(decks ? { decks: [decks[0], decks[1]] as [DeckList, DeckList] } : {}),
      ...(startSeed !== undefined ? { startSeed } : {}),
      ...(over
        ? {
          result: {
            winner: 'winner' in over ? (over.winner as PlayerId | null) : null,
            points: [state.players[0].points, state.players[1].points] as [number, number],
            turns: state.turn,
          },
        }
        : {}),
    };
  }

  /** Drops the last  actions and the entries that described them. */
  rewind(n: number): void {
    this.actions.splice(this.actions.length - n, n);
    this.entries.splice(this.entries.length - n, n);
    this.seq = this.entries.length;
  }

  clear(): void {
    this.entries.length = 0;
    this.actions.length = 0;
    this.seq = 0;
  }

  // -- putting it into words -------------------------------------------------

  private name(card: string | undefined): string {
    if (!card) return 'a card';
    return this.rules.index.has(card) ? this.rules.index.get(card).name : card;
  }

  private cardOf(state: GameState, id: number | undefined): string {
    if (id !== undefined && state.objects[id]) return this.name(state.objects[id]!.card);
    return 'a card';
  }

  private describeAction(state: GameState, a: Action): string {
    // An object keeps its identity across zones, so the post-action state still
    // knows which card this was.
    const named = (id: number | undefined): string => this.cardOf(state, id);
    switch (a.t) {
      case 'play': return `Play ${named(a.object)}${place(a.to ?? null)}`;
      case 'activate': return `Use ${named(a.object)}'s ability`;
      case 'move': return `Move ${a.objects.length > 1 ? `${a.objects.length} units` : named(a.objects[0])}${place(a.to)}`;
      case 'hide': return `Hide ${named(a.object)} at battlefield ${a.battlefield + 1}`;
      case 'mulligan': return a.cards.length === 0 ? 'Keep hand' : `Mulligan ${a.cards.length}`;
      case 'assignDamage': return 'Assign combat damage';
      case 'choose': return `Answer: option ${a.pick}`;
      case 'endTurn': return 'End turn';
      case 'pass': return 'Pass';
      case 'concede': return 'Concede';
      default: return (a as { t: string }).t;
    }
  }

  private describeEvent(e: GameEvent): string | null {
    const who = (p: PlayerId): string => (p === this.human ? 'You' : 'Opponent');
    switch (e.t) {
      case 'draw': return `${who(e.player)} drew ${this.name(e.card)}`;
      case 'channel': return `${who(e.player)} channelled ${this.name(e.card)}`;
      case 'play': return `${this.name(e.card)} was played${place(e.to)}`;
      case 'resolve': return `${this.name(e.card)} resolved`;
      case 'counter': return 'A spell was countered';
      case 'damage': return `${e.amount} damage dealt`;
      case 'kill': return `${this.name(e.card)} died`;
      case 'discard': return `${who(e.player)} discarded ${this.name(e.card)}`;
      case 'recycle': return `${who(e.player)} recycled ${e.objects.length}`;
      case 'score': return `${who(e.player)} scored ${e.points} by ${e.method} at battlefield ${e.battlefield + 1}`;
      case 'pointsGained': return `${who(e.player)} now has ${e.total}`;
      // 471.1.b. Said in full, because from the table this looks like a point
      // that simply failed to arrive: the battlefield was conquered, the score
      // did not move, and a card appeared in hand instead. Naming the rule is
      // the difference between a confusing non-event and a rule being applied.
      case 'finalPointDenied':
        return `${who(e.player)} conquered battlefield ${e.battlefield + 1} but drew a card `
          + 'instead of the final point -- a conquer only pays it once every battlefield '
          + 'has been scored this turn (471.1.b)';
      case 'controlChanged': return e.to === null
        ? `Battlefield ${e.battlefield + 1} is uncontrolled`
        : `${who(e.to)} controls battlefield ${e.battlefield + 1}`;
      case 'combatBegin': return `Combat at battlefield ${e.battlefield + 1}`;
      case 'combatResult': return e.winner === null
        ? 'Combat was a draw'
        : `${who(e.winner)} won the combat`;
      case 'showdownBegin': return `Showdown at battlefield ${e.battlefield + 1}`;
      case 'burnOut': return `${who(e.player)} burnt out`;
      case 'hide': return `${who(e.player)} hid a card at battlefield ${e.battlefield + 1}`;
      case 'gameOver': return e.winner === null
        ? `Game over: ${e.reason}`
        : `${who(e.winner)} won: ${e.reason}`;
      // Bookkeeping the player can already see on the board.
      case 'phase': case 'priority': case 'focus': case 'exhaust': case 'ready':
      case 'poolEmptied': case 'addResources': case 'turnEnd': case 'heal':
      case 'mulligan': case 'move': case 'recall': case 'contest':
      case 'combatDamage': case 'combatEnd': case 'showdownEnd':
      case 'lookFacedown':
        return null;
      default: return null;
    }
  }
}

function place(to: Location | null): string {
  if (!to) return '';
  return to.kind === 'battlefield' ? ` to battlefield ${to.index + 1}` : ' to base';
}

function describeTrace(t: AiTrace): string {
  if (t.blundered) return `deliberate blunder (difficulty), ${t.considered} options`;
  const margin = t.runnerUp > -Infinity ? `, next best ${t.runnerUp.toFixed(1)}` : '';
  return `scored ${t.score.toFixed(1)} of ${t.evaluated}/${t.considered} looked at${margin}`;
}
